import { Cesium3DTileStyle, defined, type Cesium3DTileset } from "cesium";

export type TilestyleConditions = {
    [key: string]: {
        show: boolean
    }
}

const is_lavspent = "${CityFurniture.class} === '3D_MastElGr'";
const is_belysning = "${CityFurniture.class} === '3D_Belysningsmast'";
const is_hoyspent = "${CityFurniture.class} === '3D_Trase'";

export const readableConditions = {
    "3D_MastElGr": 'Vis lavspent',
    "3D_Belysningsmast": 'Vis belysningsmast',
    "3D_Trase": 'Vis høyspent'
} as const;

export const tileStyleConditions: TilestyleConditions = {
    '3D_MastElGr': {
		show: false
	},
    "3D_Belysningsmast" : {
        show: false
    },
    "3D_Trase": {
        show: false
    },
}

const defaultTileStyle = {
    defines: {
      "is_roof": "${BuildingRoofSurface} !== undefined",
      "is_wall": "${BuildingWallSurface} !== undefined",
      "is_bolig": "${Building.function} === 'Bolig' || ${Building.function} === 'Fritidsbolig'",
      "is_hytte": "${Building.class} === '161'",
      "is_laave": "${Building.class} === '241'",
      "is_veksthus": "${Building.class} === '243'",
      "is_divlarge": "${Building.function} === 'Garasje uthus' || ${Building.function} === 'Fabrikk naering' || ${Building.function} ==='Landbruk fiske'|| ${Building.function} === 'Samferdsel'|| ${Building.function} === 'Udefinert'|| ${Building.function} === 'Kontor'|| ${Building.function} === 'Handel'|| ${Building.function} === 'Overnatting'|| ${Building.function} === 'Restaurant'|| ${Building.function} === 'Undervisning'|| ${Building.function} === 'Kultur'|| ${Building.function} === 'Idrett'|| ${Building.function} === 'Kirke'|| ${Building.function} === 'Helse'",
      "is_lavspent": "${CityFurniture.class} === '3D_MastElGr'",
      "is_belysning": "${CityFurniture.class} === '3D_Belysningsmast'",
      "is_hoyspent": "${CityFurniture.class} === '3D_Trase'",
      "is_hekk": "${CityFurniture.class} === '3D_Hekk'",
      "glass_color": "rgba(255*.4, 255*.4, 255*.5, .75)",
      "roof_color": "rgb(255*.4, 255*.17, 255*.11)",
      "wall_color": "rgb(255*.6, 255*.6,  255*.6)",
      "barnred_color": "rgb(255*.5, 255*.15, 255*.15)",
      "tarbrown_color": "rgb(255*.6, 255*.18, 255*.1)",
      "darkgray_color": "rgb(255*.2, 255*.2,  255*.2)",
      "medgray_color": "rgb(255*.5, 255*.5,  255*.5)",
      "vegetation_color": "rgb(14, 127, 19, 255*.5)",
      "lightgray_color": "rgb(255*.8, 255*.8,  255*.8)"
    },
    show: {
      conditions: [
        [
          "${is_lavspent}",
          "false"
        ],
        [
          "${is_belysning}",
          "false"
        ],
        [
          "${is_hoyspent}",
          "false"
        ],
        [
          "true",
          "true"
        ]
      ]
    },
    color: {
      conditions: [
        [
          "${is_veksthus}",
          "${glass_color}"
        ],
        [
          "${is_roof} && ${is_hytte}",
          "${medgray_color}"
        ],
        [
          "${is_wall} && ${is_hytte}",
          "${tarbrown_color}"
        ],
        [
          "${is_roof} && ${is_laave}",
          "${medgray_color}"
        ],
        [
          "${is_wall} && ${is_laave}",
          "${barnred_color}"
        ],
        [
          "${is_roof} && ${is_bolig}",
          "${roof_color}"
        ],
        [
          "${is_wall} && ${is_bolig}",
          "${wall_color}"
        ],
        [
          "${is_roof} && ${is_divlarge}",
          "${darkgray_color}"
        ],
        [
          "${is_hekk}",
          "${vegetation_color}"
        ],
        [
          "true",
          "${lightgray_color}"
        ]
      ]
    }
  }

export const apply3DTileStyles = async (tileset: Cesium3DTileset, styles: {
    [key: string]: {
        show: boolean
    }
}) => {
    if(!styles){
        styles = tileStyleConditions;
    }
    const styleObject: {
        show: {
            conditions: (string | boolean)[][]
        }
    } = {
        show: {
            conditions: []
        }
    };

    const styleKeys = Object.keys(styles) as Array<keyof typeof styles>;

    let finalCondition;

    for (let i = 0; i < styleKeys.length; ++i) {
        const key = styleKeys[i];

        const showCondition = [
            `\${CityFurniture.class} === '${key}'`,
            styles[key].show,
        ];

        if (i === -1) {
            showCondition[0] = true;

            finalCondition = {
                showCondition: showCondition,
            };
        } else {
            styleObject.show.conditions.push(showCondition);
        }
    }

    if (defined(finalCondition)) {
        styleObject.show.conditions.push(finalCondition.showCondition);
    }

    const updatedStyle = {
        ...defaultTileStyle,
        ...styleObject
    };
    tileset.style = new Cesium3DTileStyle(updatedStyle);
}

  /**
   * Updates Norkart 3D tile style. If optional tileset is provided, this function will apply the new style.
   * @param options 
   * @param tileset 
   * @returns 
   */
  const update3dTileStyle = (options: {
    showLv: boolean,
    showHv: boolean,
    showLight: boolean
  }, applyToTileset?: Cesium3DTileset) => {

    let selectedStyleObject = defaultTileStyle;

    selectedStyleObject.show.conditions = [
        [
            is_lavspent,
            options.showLv ? "true" : "false"
        ],
        [
            is_belysning,
            options.showLight ? "true" : "false"
        ],
        [
            is_hoyspent,
            options.showHv ? "true" : "false"
        ],
        [
            "true",
            "true"
        ]
    ]

    if (defined(applyToTileset)) {
        try {
            applyToTileset.style = new Cesium3DTileStyle(
                selectedStyleObject
            );
        } catch (e) {
            console.error('Kunne ikke style');
        }
    }

    return selectedStyleObject;

  }

  

export default update3dTileStyle;